<template>
    <templatesite>
        <template v-slot:appheader>
            <appheader></appheader>
        </template>
        <template v-slot:appsidebar>
            <appsidebar></appsidebar>
        </template>
        <template v-slot:appcontent>
            <div class="content">
                <div class="main-content cxs-page">

                    <!-- En-tête : salutation, titre, compteurs -->
                    <div class="cxs-head">
                        <div>
                            <div class="cxs-hello">
                                {{ salutation }}<template v-if="prenom"> {{ prenom }}</template>
                                · <span>{{ datedujour }}</span> · <span class="cxs-clock">{{ heure }}</span>
                            </div>
                            <h1>Choisissez votre caisse</h1>
                        </div>
                        <div class="cxs-stats">
                            <div class="cxs-stat cxs-stat--ok"><i class="ri-checkbox-circle-line"></i><div><b>{{ compte.open }}</b><span>ouverte{{ compte.open > 1 ? 's' : '' }}</span></div></div>
                            <div v-if="compte.stale" class="cxs-stat cxs-stat--warn"><i class="ri-error-warning-line"></i><div><b>{{ compte.stale }}</b><span>à clôturer</span></div></div>
                            <div class="cxs-stat cxs-stat--off"><i class="ri-lock-line"></i><div><b>{{ compte.closed }}</b><span>fermée{{ compte.closed > 1 ? 's' : '' }}</span></div></div>
                        </div>
                    </div>

                    <!-- Aucune caisse attribuée -->
                    <div v-if="listeallcaisses.length === 0" class="cxs-empty">
                        <i class="ri-safe-2-line"></i>
                        <h3>Aucune caisse ne vous est attribuée</h3>
                        Demandez à votre administrateur de vous donner accès à une caisse (Paramètres > Utilisateurs).
                    </div>

                    <template v-else>

                        <!-- Reprendre la dernière caisse utilisée sur ce poste -->
                        <div v-if="dernierecaisse" class="cxs-resume" role="button" tabindex="0"
                             @click="selectCaisse(dernierecaisse)" @keydown.enter="selectCaisse(dernierecaisse)">
                            <span class="cxs-resume__ic"><i class="ri-history-line"></i></span>
                            <div>
                                <small>Reprendre où vous en étiez</small>
                                <h2>{{ dernierecaisse.name }}</h2>
                                <p>{{ resumeetat(dernierecaisse) }} · dernière utilisation sur ce poste</p>
                            </div>
                            <button type="button" class="cxs-resume__go">Reprendre <kbd>Entrée</kbd></button>
                        </div>

                        <!-- Recherche, filtres, affichage -->
                        <div class="cxs-toolbar">
                            <label class="cxs-search">
                                <i class="ri-search-line"></i>
                                <input ref="recherche" v-model="searchQuery" type="text" placeholder="Rechercher une caisse..." autocomplete="off"
                                       @keydown.esc="searchQuery = ''; $event.target.blur()"
                                       @keydown.enter.prevent="ouvrirpremier">
                                <button v-if="searchQuery" type="button" class="cxs-search__clr" title="Effacer" @click="searchQuery = ''; $refs.recherche.focus()"><i class="ri-close-circle-fill"></i></button>
                                <kbd>/</kbd>
                            </label>
                            <div class="cxs-chips">
                                <button type="button" class="cxs-chip" :class="{ on: filtre === 'tous' }" @click="filtre = 'tous'">Toutes <b>{{ listeallcaisses.length }}</b></button>
                                <button type="button" class="cxs-chip" :class="{ on: filtre === 'open' }" @click="filtre = 'open'">Ouvertes <b>{{ compte.open + compte.stale }}</b></button>
                                <button type="button" class="cxs-chip" :class="{ on: filtre === 'closed' }" @click="filtre = 'closed'">Fermées <b>{{ compte.closed }}</b></button>
                            </div>
                            <div class="cxs-views">
                                <button type="button" :class="{ on: affichage === 'grille' }" title="Grille" @click="changeraffichage('grille')"><i class="ri-layout-grid-line"></i></button>
                                <button type="button" :class="{ on: affichage === 'liste' }" title="Liste" @click="changeraffichage('liste')"><i class="ri-list-check"></i></button>
                            </div>
                        </div>

                        <!-- Aucun résultat -->
                        <div v-if="filteredCaisses.length === 0" class="cxs-empty">
                            <i class="ri-search-eye-line"></i>
                            <h3>Aucune caisse trouvée</h3>
                            <template v-if="searchQuery">Aucun résultat pour « {{ searchQuery }} ».</template>
                            <br>
                            <button type="button" class="ds-btn-ghost cxs-btn" @click="searchQuery = ''; filtre = 'tous'"><i class="ri-refresh-line"></i>Tout afficher</button>
                        </div>

                        <!-- Groupes : ouvertes puis fermées -->
                        <template v-for="groupe in groupes" :key="groupe.titre">
                            <div class="cxs-sec">{{ groupe.titre }} · {{ groupe.items.length }}</div>

                            <div v-if="affichage === 'grille'" class="cxs-grid">
                                <div v-for="caisse in groupe.items" :key="caisse.pid"
                                     class="cxs-card" :class="'cxs--' + etat(caisse)"
                                     tabindex="0" role="button" :aria-label="'Accéder à la caisse ' + caisse.name"
                                     @click="selectCaisse(caisse)"
                                     @keydown.enter="selectCaisse(caisse)" @keydown.space.prevent="selectCaisse(caisse)">
                                    <div class="cxs-card__row">
                                        <span class="cxs-ico"><i :class="etat(caisse) === 'closed' ? 'ri-safe-line' : 'ri-safe-2-line'"></i></span>
                                        <div class="cxs-card__nm">
                                            <b v-html="surligner(caisse.name)"></b>
                                            <span class="cxs-pill" :class="'cxs-pill--' + etat(caisse)">{{ libelleetat(caisse) }}</span>
                                        </div>
                                        <span v-if="rang(caisse) <= 9" class="cxs-key">{{ rang(caisse) }}</span>
                                    </div>
                                    <div v-if="etat(caisse) === 'stale'" class="cxs-warn">
                                        <i class="ri-error-warning-fill"></i>
                                        <span>Ouverte depuis {{ joursouverture(caisse) }} jour{{ joursouverture(caisse) > 1 ? 's' : '' }} : à clôturer avant de vendre aujourd'hui.</span>
                                    </div>
                                    <div class="cxs-card__meta">
                                        <span class="cxs-when"><i :class="iconequand(caisse)"></i><span v-html="textequand(caisse)"></span></span>
                                        <span class="cxs-cta">{{ etat(caisse) === 'closed' ? 'Ouvrir' : 'Accéder' }}<i class="ri-arrow-right-line"></i></span>
                                    </div>
                                </div>
                            </div>

                            <div v-else class="cxs-list">
                                <div v-for="caisse in groupe.items" :key="caisse.pid"
                                     class="cxs-li" :class="'cxs--' + etat(caisse)"
                                     tabindex="0" role="button" :aria-label="'Accéder à la caisse ' + caisse.name"
                                     @click="selectCaisse(caisse)"
                                     @keydown.enter="selectCaisse(caisse)" @keydown.space.prevent="selectCaisse(caisse)">
                                    <span class="cxs-ico"><i class="ri-safe-2-line"></i></span>
                                    <b v-html="surligner(caisse.name)"></b>
                                    <span class="cxs-li__c3"><span class="cxs-pill" :class="'cxs-pill--' + etat(caisse)">{{ libelleetat(caisse) }}</span></span>
                                    <span class="cxs-when cxs-li__c4"><i :class="iconequand(caisse)"></i><span v-html="textequand(caisse)"></span></span>
                                    <span class="cxs-li__go">{{ etat(caisse) === 'closed' ? 'Ouvrir' : 'Accéder' }}<i class="ri-arrow-right-s-line"></i></span>
                                </div>
                            </div>
                        </template>

                    </template>

                </div>
            </div>

            <!-- Chargement de la caisse choisie -->
            <transition name="fade">
                <div v-if="isLoading" class="cxs-loading">
                    <div class="cxs-loading__box">
                        <span class="cxs-loading__sp"></span>
                        <div><b>{{ caisseencours }}</b><span>Ouverture de la caisse...</span></div>
                    </div>
                </div>
            </transition>

        </template>
    </templatesite>
</template>

<script>
    import templatesite from "@/components/template/apptemplate";
    import appheader from "@/components/template/appheader";
    import appsidebar from "@/components/template/appsidebar";
    import { mapGetters } from 'vuex';

    /* Dernière caisse choisie sur ce poste (pid) et mode d'affichage. */
    const CLE_DERNIERE = 'caisse_derniere_v1';
    const CLE_AFFICHAGE = 'caisse_affichage_v1';

    function lirelocal(cle, defaut) {
        try { return localStorage.getItem(cle) || defaut; } catch (e) { return defaut; }
    }

    function echapper(t) {
        return String(t).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    export default {
        name: "CaisseList",

        components: {
            templatesite,
            appheader,
            appsidebar,
        },

        data() {
            return {
                searchQuery: '',
                isLoading: false,
                caisseencours: '',
                filtre: 'tous',
                affichage: lirelocal(CLE_AFFICHAGE, 'grille'),
                piddernierecaisse: lirelocal(CLE_DERNIERE, ''),
                maintenant: new Date(),
                minuterie: null,
            };
        },

        computed: {
            ...mapGetters(['listeallcaisses']),

            aujourdhui() {
                const d = this.maintenant;
                return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
            },

            salutation() {
                const h = this.maintenant.getHours();
                return h < 13 ? 'Bonjour' : (h < 18 ? 'Bon après-midi' : 'Bonsoir');
            },

            prenom() {
                return String(this.$store.state.appusername || '').trim().split(/\s+/)[0] || '';
            },

            datedujour() {
                return this.maintenant.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
            },

            heure() {
                return String(this.maintenant.getHours()).padStart(2, '0') + ':' + String(this.maintenant.getMinutes()).padStart(2, '0');
            },

            compte() {
                const c = { open: 0, stale: 0, closed: 0 };
                this.listeallcaisses.forEach(x => { c[this.etat(x)]++; });
                return c;
            },

            dernierecaisse() {
                if (!this.piddernierecaisse) return null;
                return this.listeallcaisses.find(c => c.pid === this.piddernierecaisse) || null;
            },

            // Filtre les caisses selon la recherche et l'état
            filteredCaisses() {
                const query = this.searchQuery.toLowerCase().trim();
                return this.listeallcaisses.filter(caisse => {
                    if (query && !String(caisse.name || '').toLowerCase().includes(query)) return false;
                    if (this.filtre === 'open') return this.etat(caisse) !== 'closed';
                    if (this.filtre === 'closed') return this.etat(caisse) === 'closed';
                    return true;
                });
            },

            // À clôturer d'abord, puis ouvertes, puis fermées
            groupes() {
                const ordre = { stale: 0, open: 1, closed: 2 };
                const ouvertes = this.filteredCaisses.filter(c => this.etat(c) !== 'closed')
                    .sort((a, b) => ordre[this.etat(a)] - ordre[this.etat(b)]);
                const fermees = this.filteredCaisses.filter(c => this.etat(c) === 'closed');
                return [
                    { titre: 'Ouvertes', items: ouvertes },
                    { titre: 'Fermées', items: fermees },
                ].filter(g => g.items.length);
            },

            // Ordre d'affichage, pour les raccourcis 1 à 9
            ordreaffiche() {
                return this.groupes.reduce((acc, g) => acc.concat(g.items), []);
            },
        },

        methods: {
            /* État dérivé des seuls champs disponibles : opencaisse et dateopen. */
            etat(caisse) {
                if (caisse.opencaisse !== 1) return 'closed';
                const jour = String(caisse.dateopen || '').slice(0, 10);
                return jour && jour < this.aujourdhui ? 'stale' : 'open';
            },

            libelleetat(caisse) {
                return { open: 'Ouverte', stale: 'À clôturer', closed: 'Fermée' }[this.etat(caisse)];
            },

            datefr(d) {
                const [y, m, j] = String(d).slice(0, 10).split('-');
                return j + '/' + m + '/' + y;
            },

            joursouverture(caisse) {
                const debut = new Date(String(caisse.dateopen).slice(0, 10) + 'T00:00:00');
                const fin = new Date(this.aujourdhui + 'T00:00:00');
                return Math.max(1, Math.round((fin - debut) / 864e5));
            },

            iconequand(caisse) {
                const e = this.etat(caisse);
                return e === 'open' ? 'ri-time-line' : (e === 'stale' ? 'ri-calendar-line' : 'ri-lock-line');
            },

            textequand(caisse) {
                const e = this.etat(caisse);
                if (e === 'open') return 'Ouverte à <b>' + echapper(String(caisse.dateopen).slice(11, 16)) + '</b>';
                if (e === 'stale') return 'Journée du <b>' + echapper(this.datefr(caisse.dateopen)) + '</b>';
                return caisse.dateopen ? 'Dernière ouverture <b>' + echapper(this.datefr(caisse.dateopen)) + '</b>' : 'Jamais ouverte';
            },

            resumeetat(caisse) {
                const e = this.etat(caisse);
                if (e === 'open') return "Ouverte aujourd'hui à " + String(caisse.dateopen).slice(11, 16);
                if (e === 'stale') return 'Journée du ' + this.datefr(caisse.dateopen) + ' non clôturée';
                return 'Fermée';
            },

            surligner(nom) {
                const t = String(nom || '');
                const q = this.searchQuery.trim();
                const i = q ? t.toLowerCase().indexOf(q.toLowerCase()) : -1;
                if (i < 0) return echapper(t);
                return echapper(t.slice(0, i)) + '<mark>' + echapper(t.slice(i, i + q.length)) + '</mark>' + echapper(t.slice(i + q.length));
            },

            rang(caisse) {
                return this.ordreaffiche.indexOf(caisse) + 1;
            },

            changeraffichage(v) {
                this.affichage = v;
                try { localStorage.setItem(CLE_AFFICHAGE, v); } catch (e) { /* stockage indisponible */ }
            },

            ouvrirpremier() {
                if (this.ordreaffiche.length) this.selectCaisse(this.ordreaffiche[0]);
            },

            /* « / » : recherche ; 1 à 9 : caisse affichée ; Entrée : reprendre. */
            raccourcisclavier(e) {
                if (this.isLoading) return;
                const tag = (document.activeElement && document.activeElement.tagName) || '';
                if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
                if (e.key === '/') {
                    if (this.$refs.recherche) { e.preventDefault(); this.$refs.recherche.focus(); }
                } else if (/^[1-9]$/.test(e.key)) {
                    const c = this.ordreaffiche[Number(e.key) - 1];
                    if (c) this.selectCaisse(c);
                } else if (e.key === 'Enter' && document.activeElement === document.body && this.dernierecaisse) {
                    this.selectCaisse(this.dernierecaisse);
                }
            },

            async selectCaisse(caisse) {
                // Feedback visuel
                this.isLoading = true;
                this.caisseencours = caisse.name;

                try { localStorage.setItem(CLE_DERNIERE, caisse.pid); } catch (e) { /* stockage indisponible */ }

                try {
                    // Reset du state
                    await Promise.all([
                        this.$store.dispatch('caisseencour', 0),
                        this.$store.dispatch('montantencaisse', 0),
                        this.$store.dispatch('dateouverturecaisse', ''),
                        this.$store.dispatch('ouverturedelacaisse', 0),
                        this.$store.dispatch('caisseinfo', {}),
                    ]);

                    // Set de la nouvelle caisse
                    await this.$store.dispatch('caisseencour', caisse.pid);

                    // Navigation
                    this.$router.push({
                        name: 'caisseencour',
                        params: { societe: this.$store.state.appsociete }
                    });

                } catch (error) {
                    console.error('Erreur lors de la sélection de la caisse:', error);
                    // Ici vous pourriez ajouter une notification d'erreur
                } finally {
                    this.isLoading = false;
                }
            },
        },

        mounted() {
            document.addEventListener('keydown', this.raccourcisclavier);
            this.minuterie = setInterval(() => { this.maintenant = new Date(); }, 15000);
        },

        beforeUnmount() {
            document.removeEventListener('keydown', this.raccourcisclavier);
            clearInterval(this.minuterie);
        },
    };
</script>

<style scoped>
    /* Sélection de caisse (charte cf-/ds-, 2026-10-25). Préfixe cxs-. */
    .cxs-page { padding: 1.5rem 1.5rem 2.5rem; background: #f3f4f8; min-height: calc(100vh - 60px); }
    .cxs-btn { padding: 8px 13px; margin-top: 12px; }

    /* En-tête */
    .cxs-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 20px; }
    .cxs-hello { font-size: .8rem; color: var(--cf-muted); font-weight: 600; }
    .cxs-hello span:first-of-type { text-transform: capitalize; }
    .cxs-clock { font-family: ui-monospace, "Cascadia Mono", Consolas, monospace; color: var(--cf-ink); font-weight: 700; }
    .cxs-head h1 { margin: 4px 0 0; font-size: 1.5rem; font-weight: 800; color: var(--cf-ink-strong); letter-spacing: -.02em; }
    .cxs-stats { display: flex; gap: 8px; flex-wrap: wrap; }
    .cxs-stat { display: flex; align-items: center; gap: 10px; background: #fff; border: 1px solid var(--cf-border); border-radius: 12px; padding: 9px 14px; box-shadow: var(--cf-shadow-sm); }
    .cxs-stat > i { width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center; font-size: 16px; }
    .cxs-stat b { display: block; font-family: ui-monospace, "Cascadia Mono", Consolas, monospace; font-size: 1.1rem; font-weight: 800; color: var(--cf-ink-strong); line-height: 1.1; }
    .cxs-stat span { font-size: .7rem; color: var(--cf-muted); font-weight: 600; }
    .cxs-stat--ok > i { background: rgb(var(--success) / .12); color: rgb(var(--success)); }
    .cxs-stat--off > i { background: var(--cf-surface-alt); color: var(--cf-muted); }
    .cxs-stat--warn > i { background: rgb(var(--warning) / .2); color: #b7791f; }

    /* Reprendre */
    .cxs-resume { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 18px; border-radius: 16px; padding: 18px 22px; margin-bottom: 22px; color: #e2e8f0; cursor: pointer; position: relative; overflow: hidden; outline: none;
        background: radial-gradient(110% 160% at 100% 0%, rgb(var(--primary) / .6) 0%, transparent 55%), linear-gradient(160deg, #1e1b3a 0%, #0f172a 100%);
        box-shadow: 0 16px 32px -16px rgb(76 50 160 / .6); transition: transform .18s; }
    .cxs-resume:hover, .cxs-resume:focus-visible { transform: translateY(-2px); }
    .cxs-resume::after { content: ""; position: absolute; left: 0; right: 0; bottom: -1px; height: 10px; background: radial-gradient(circle at 7px 10px, #f3f4f8 6px, transparent 6.5px) 0 0 / 14px 10px repeat-x; }
    .cxs-resume__ic { width: 52px; height: 52px; border-radius: 14px; background: rgb(255 255 255 / .1); display: grid; place-items: center; font-size: 26px; color: #c4b5fd; }
    .cxs-resume small { font-size: .65rem; text-transform: uppercase; letter-spacing: .12em; color: #a5b4fc; font-weight: 700; }
    .cxs-resume h2 { margin: 2px 0; font-size: 1.25rem; font-weight: 800; color: #fff; }
    .cxs-resume p { margin: 0; font-size: .75rem; color: #94a3b8; }
    .cxs-resume__go { display: inline-flex; align-items: center; gap: 8px; background: #fff; color: rgb(var(--primary)); font-weight: 800; font-size: .8rem; padding: 10px 16px; border-radius: 10px; border: 0; cursor: pointer; white-space: nowrap; }
    .cxs-resume__go kbd { font-family: ui-monospace, Consolas, monospace; font-size: .65rem; border: 1px solid rgb(var(--primary) / .3); border-radius: 4px; padding: 0 5px; background: transparent; color: inherit; }

    /* Barre d'outils */
    .cxs-toolbar { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-bottom: 6px; }
    .cxs-search { position: relative; flex: 1; min-width: 240px; max-width: 420px; margin: 0; }
    .cxs-search > i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--cf-faint); font-size: 16px; }
    .cxs-search input { width: 100%; height: 42px; border: 1px solid rgb(var(--input-border, 203 213 225)); border-radius: 10px; padding: 0 70px 0 38px; font-size: .85rem; outline: none; background: #fff; color: var(--cf-ink); }
    .cxs-search input:focus { border-color: rgb(var(--primary)); box-shadow: 0 0 0 3px rgb(var(--primary) / .1); }
    .cxs-search__clr { position: absolute; right: 38px; top: 50%; transform: translateY(-50%); border: 0; background: transparent; color: var(--cf-faint); cursor: pointer; font-size: 16px; }
    .cxs-search kbd { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); font-family: ui-monospace, Consolas, monospace; font-size: .65rem; color: var(--cf-faint); border: 1px solid var(--cf-border); border-radius: 4px; padding: 1px 5px; background: transparent; }
    .cxs-chips { display: inline-flex; background: #fff; border: 1px solid var(--cf-border); border-radius: 10px; padding: 3px; gap: 2px; }
    .cxs-chip { border: 0; background: transparent; border-radius: 7px; font-size: .75rem; font-weight: 700; color: var(--cf-muted); padding: 7px 12px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
    .cxs-chip b { font-family: ui-monospace, Consolas, monospace; font-size: .68rem; background: var(--cf-surface-alt); border-radius: 99px; padding: 0 6px; color: var(--cf-muted); }
    .cxs-chip.on { background: rgb(var(--primary) / .1); color: rgb(var(--primary)); }
    .cxs-chip.on b { background: rgb(var(--primary)); color: #fff; }
    .cxs-views { margin-left: auto; display: inline-flex; background: #fff; border: 1px solid var(--cf-border); border-radius: 10px; padding: 3px; }
    .cxs-views button { border: 0; background: transparent; width: 34px; height: 32px; border-radius: 7px; color: var(--cf-muted); cursor: pointer; font-size: 16px; }
    .cxs-views button.on { background: var(--cf-surface-alt); color: var(--cf-ink-strong); }

    .cxs-sec { font-size: .68rem; text-transform: uppercase; letter-spacing: .1em; color: var(--cf-faint); font-weight: 800; margin: 20px 0 10px; display: flex; align-items: center; gap: 10px; }
    .cxs-sec::after { content: ""; flex: 1; height: 1px; background: var(--cf-border); }

    /* Couleur d'état commune (cartes et lignes) */
    .cxs--open { --cxs-bar: rgb(var(--success)); }
    .cxs--stale { --cxs-bar: rgb(var(--warning)); }
    .cxs--closed { --cxs-bar: var(--cf-border); }
    .cxs-ico { width: 44px; height: 44px; border-radius: 12px; display: grid; place-items: center; font-size: 21px; flex: none; transition: .18s; }
    .cxs--open .cxs-ico { background: rgb(var(--success) / .12); color: rgb(var(--success)); }
    .cxs--stale .cxs-ico { background: rgb(var(--warning) / .2); color: #b7791f; }
    .cxs--closed .cxs-ico { background: var(--cf-surface-alt); color: var(--cf-faint); }
    .cxs-pill { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 999px; font-size: .68rem; font-weight: 700; white-space: nowrap; }
    .cxs-pill::before { content: ""; width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
    .cxs-pill--open { background: rgb(var(--success) / .12); color: #0f8a68; }
    .cxs-pill--open::before { animation: cxs-pulse 2s infinite; }
    .cxs-pill--stale { background: rgb(var(--warning) / .2); color: #a16207; }
    .cxs-pill--closed { background: var(--cf-surface-alt); color: var(--cf-muted); }
    @keyframes cxs-pulse { 0%,100% { box-shadow: 0 0 0 2px rgb(var(--success) / .25); } 50% { box-shadow: 0 0 0 5px rgb(var(--success) / 0); } }
    .cxs-when { display: inline-flex; align-items: center; gap: 5px; font-size: .72rem; color: var(--cf-muted); }
    .cxs-when :deep(b) { font-family: ui-monospace, "Cascadia Mono", Consolas, monospace; color: var(--cf-ink); }
    :deep(mark) { background: rgb(var(--warning) / .4); color: inherit; border-radius: 2px; padding: 0; }

    /* Grille */
    .cxs-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 14px; }
    .cxs-card { position: relative; background: #fff; border: 1px solid var(--cf-border); border-radius: 14px; padding: 16px 16px 14px; cursor: pointer; box-shadow: var(--cf-shadow-sm); transition: transform .18s, box-shadow .18s, border-color .18s; display: flex; flex-direction: column; gap: 12px; outline: none; }
    .cxs-card::before { content: ""; position: absolute; left: 16px; right: 16px; top: 0; height: 3px; border-radius: 0 0 4px 4px; background: var(--cxs-bar); }
    .cxs-card:hover, .cxs-card:focus-visible { transform: translateY(-3px); box-shadow: 0 14px 28px -14px rgb(15 23 42 / .25); border-color: rgb(var(--primary) / .45); }
    .cxs-card:hover .cxs-ico, .cxs-card:focus-visible .cxs-ico { background: rgb(var(--primary)); color: #fff; }
    .cxs-card__row { display: flex; align-items: center; gap: 12px; }
    .cxs-card__nm { flex: 1; min-width: 0; }
    .cxs-card__nm b { display: block; font-size: .9rem; font-weight: 800; color: var(--cf-ink-strong); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 3px; }
    .cxs-key { font-family: ui-monospace, Consolas, monospace; font-size: .65rem; color: var(--cf-faint); border: 1px solid var(--cf-border); border-radius: 5px; padding: 1px 6px; align-self: flex-start; }
    .cxs-warn { font-size: .72rem; color: #a16207; background: rgb(var(--warning) / .12); border-radius: 8px; padding: 7px 9px; display: flex; gap: 6px; align-items: flex-start; }
    .cxs-card__meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding-top: 12px; border-top: 1px dashed var(--cf-border); }
    .cxs-cta { display: inline-flex; align-items: center; gap: 4px; font-weight: 800; color: rgb(var(--primary)); font-size: .75rem; opacity: 0; transform: translateX(-4px); transition: .18s; }
    .cxs-card:hover .cxs-cta, .cxs-card:focus-visible .cxs-cta { opacity: 1; transform: none; }

    /* Liste */
    .cxs-list { background: #fff; border: 1px solid var(--cf-border); border-radius: 14px; overflow: hidden; box-shadow: var(--cf-shadow-sm); }
    .cxs-li { display: grid; grid-template-columns: 40px 1fr 150px 210px 110px; align-items: center; gap: 12px; padding: 11px 16px; border-bottom: 1px solid var(--cf-surface-alt); cursor: pointer; outline: none; border-left: 3px solid var(--cxs-bar); }
    .cxs-li:last-child { border-bottom: 0; }
    .cxs-li:hover, .cxs-li:focus-visible { background: var(--cf-surface); }
    .cxs-li .cxs-ico { width: 34px; height: 34px; border-radius: 10px; font-size: 17px; }
    .cxs-li > b { font-weight: 700; color: var(--cf-ink-strong); font-size: .85rem; }
    .cxs-li__go { justify-self: end; font-weight: 800; color: rgb(var(--primary)); font-size: .75rem; display: inline-flex; align-items: center; gap: 3px; }

    /* Vide */
    .cxs-empty { text-align: center; padding: 48px 20px; background: #fff; border: 1px dashed var(--cf-border); border-radius: 16px; color: var(--cf-muted); margin-top: 14px; }
    .cxs-empty > i { font-size: 40px; color: var(--cf-faint); }
    .cxs-empty h3 { margin: 10px 0 4px; color: var(--cf-ink-strong); font-size: 1rem; font-weight: 700; }

    /* Chargement */
    .cxs-loading { position: fixed; inset: 0; background: rgb(243 244 248 / .75); backdrop-filter: blur(3px); z-index: 1000; display: flex; align-items: center; justify-content: center; }
    .cxs-loading__box { background: #fff; border: 1px solid var(--cf-border); border-radius: 16px; padding: 22px 28px; box-shadow: var(--cf-shadow-md); display: flex; align-items: center; gap: 14px; min-width: 280px; }
    .cxs-loading__sp { width: 34px; height: 34px; border-radius: 50%; border: 3px solid rgb(var(--primary) / .18); border-top-color: rgb(var(--primary)); animation: cxs-spin .8s linear infinite; flex: none; }
    .cxs-loading__box b { display: block; color: var(--cf-ink-strong); }
    .cxs-loading__box span { font-size: .75rem; color: var(--cf-muted); }
    @keyframes cxs-spin { to { transform: rotate(360deg); } }

    /* Transition pour le chargement */
    .fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
    .fade-enter-from, .fade-leave-to { opacity: 0; }

    @media (max-width: 760px) {
        .cxs-li { grid-template-columns: 36px 1fr auto; }
        .cxs-li__c3, .cxs-li__c4 { display: none; }
    }
    @media (max-width: 640px) {
        .cxs-page { padding: 1rem; }
        .cxs-head h1 { font-size: 1.25rem; }
        .cxs-stats { width: 100%; }
        .cxs-stat { flex: 1; padding: 8px 10px; }
        .cxs-resume { grid-template-columns: auto 1fr; padding: 16px; }
        .cxs-resume__go { grid-column: 1 / -1; justify-content: center; }
        .cxs-resume__go kbd, .cxs-key, .cxs-search kbd { display: none; }
        .cxs-search { max-width: none; flex-basis: 100%; }
        .cxs-search input { padding-right: 38px; }
        .cxs-search__clr { right: 10px; }
        .cxs-views { display: none; }
        .cxs-chips { width: 100%; }
        .cxs-chip { flex: 1; justify-content: center; padding: 7px 6px; }
        .cxs-grid { grid-template-columns: 1fr; }
        .cxs-cta { opacity: 1; transform: none; }
    }
    @media (prefers-reduced-motion: reduce) {
        .cxs-pill--open::before, .cxs-loading__sp { animation: none; }
    }
</style>
